/**
 * Narration engines and their voices.
 *
 * Kokoro and MeloTTS produce different waveforms, so the engine and voice are
 * part of the cache key. Existing Kokoro keys keep their old format when the
 * engine is left at its default.
 */
export type NarrationEngine = "kokoro" | "melotts";

export interface NarrationVoice {
  engine: NarrationEngine;
  /** Value that goes into the cache key. */
  id: string;
  /**
   * Delivery rate, part of the cache key. 1 is the model's own pace; MeloTTS
   * reads a little fast for a background station, so it is slowed to 0.9.
   */
  speed: number;
  /** Cache containers this producer can write, best size first. */
  codecs: readonly string[];
  /** Level trim applied after the effects chain. */
  trim: number;
  /** Whether the degrading effects run clear-only. */
  plain: boolean;
  label: string;
}

export const KOKORO_VOICE_ID = "af_heart";
export const MELOTTS_VOICE_ID = "melotts:en";
/**
 * MeloTTS reads at a brisk pace that suits a demo more than an ambient station.
 * Measured on Workers AI: 9.35 s of audio becomes 10.23 s at 0.9, a little
 * slower without sounding stretched.
 */
export const MELOTTS_SPEED = 0.9;

/**
 * Per-entry delivery rates, so the station is not one unbroken cadence.
 *
 * The choice is derived from the entry id, which makes it a property of the
 * disclosure rather than of the play: one clip per CVE at one pace, forever, and
 * the cache key stays a pure function of the entry. A rate chosen per playback
 * would multiply the key space by the number of variants and turn a miss into
 * fresh synthesis for every listener.
 */
export const MELOTTS_SPEEDS = [0.8, 0.85, 0.9, 0.95] as const;

function hash32(input: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  // FNV alone leaves the low bits tracking the last character, which is a CVE
  // id's least interesting digit. Finalize so the rate is not a function of
  // whether the number ends in 0 or 9.
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x7feb352d);
  hash ^= hash >>> 15;
  hash = Math.imul(hash, 0x846ca68b);
  hash ^= hash >>> 16;
  return hash >>> 0;
}

/** The rate this engine reads an entry at. Part of the cache key. */
export function narrationSpeed(engine: NarrationEngine, entryId: string): number {
  if (engine !== "melotts") return 1;
  return MELOTTS_SPEEDS[hash32(entryId) % MELOTTS_SPEEDS.length] ?? MELOTTS_SPEED;
}

export const NARRATION_VOICES: Record<NarrationEngine, NarrationVoice> = {
  kokoro: {
    engine: "kokoro",
    id: KOKORO_VOICE_ID,
    speed: 1,
    codecs: ["mp3", "wav"],
    trim: 1,
    plain: false,
    label: "Kokoro",
  },
  melotts: {
    engine: "melotts",
    id: MELOTTS_VOICE_ID,
    speed: MELOTTS_SPEED,
    // Workers AI returns WAV; the browser sniffs either way, but the label and
    // the key have to tell the truth for anything outside the app.
    codecs: ["wav"],
    // MeloTTS level and character are not measured yet; keep the neutral
    // profile until the audition. The key separates it from Kokoro audio.
    trim: 1,
    plain: false,
    label: "MeloTTS",
  },
};

export const NARRATION_ENGINES = ["kokoro", "melotts"] as const;

/**
 * `?tts=melotts|kokoro` wins over the build-time engine. An unknown value keeps
 * Kokoro, which is the existing/back-compatible default.
 */
export function resolveNarrationVoice(search: string, configured?: string): NarrationVoice {
  let requested: string | null = null;
  try {
    requested = new URLSearchParams(search).get("tts");
  } catch {
    requested = null;
  }
  const fromQuery = requested?.trim().toLowerCase();
  if (fromQuery === "melotts") return NARRATION_VOICES.melotts;
  if (fromQuery === "kokoro") return NARRATION_VOICES.kokoro;
  const fromBuild = configured?.trim().toLowerCase();
  return fromBuild === "melotts" ? NARRATION_VOICES.melotts : NARRATION_VOICES.kokoro;
}
